// Client-side session storage. A checked "Remember me" keeps the bearer token
// in localStorage. Leaving it unchecked keeps the token in sessionStorage, so
// closing the browser ends the sign-in. The server applies the same choice to
// the token lifetime.

const STAFF_KEY = 'fleetava_staff_session';
const CUSTOMER_KEY = 'fleetava_customer_session';
const CUSTOMER_REMEMBER_KEY = 'fleetava_customer_remember';

export interface StaffSession {
  token: string;
  user: {
    id: string;
    name: string;
    email: string;
    role: string;
    scope: 'agency' | 'platform';
    twoFactorEnabled?: boolean;
    agencyId: number | null;
  };
}

export interface CustomerSession {
  token: string;
  customer: { id: string; name: string; email: string; phone: string };
}

function readRaw(key: string): string | null {
  try {
    return window.sessionStorage.getItem(key) ?? window.localStorage.getItem(key);
  } catch {
    try {
      return window.localStorage.getItem(key);
    } catch {
      return null;
    }
  }
}

export const SESSION_CHANGE_EVENT = 'fleetava-session';

/**
 * A session is only usable when it carries a token and its subject. Older or
 * hand-edited entries (for example `{}` left by a failed sign-in) used to crash
 * the admin shell on `session.user.name`, so they are treated as signed out.
 */
function readSession<T extends { token: string }>(key: string, subject: string): T | null {
  const value = read<Record<string, unknown>>(key);
  if (!value || typeof value.token !== 'string' || value.token === '') return null;
  const subjectValue = value[subject];
  if (!subjectValue || typeof subjectValue !== 'object') return null;
  return value as unknown as T;
}

function writeRaw(key: string, value: unknown | null, remember: boolean): void {
  try {
    window.sessionStorage.removeItem(key);
    window.localStorage.removeItem(key);
    if (value !== null) {
      const storage = remember ? window.localStorage : window.sessionStorage;
      storage.setItem(key, JSON.stringify(value));
    }
  } catch {
    // Storage can be unavailable (private mode); sessions are best-effort.
  }
  notifySessionChange();
}

function notifySessionChange(): void {
  try {
    window.dispatchEvent(new Event(SESSION_CHANGE_EVENT));
  } catch {
    // document may be unavailable during tests.
  }
}

export function subscribeSessions(onChange: () => void): () => void {
  window.addEventListener(SESSION_CHANGE_EVENT, onChange);
  window.addEventListener('storage', onChange);
  return () => {
    window.removeEventListener(SESSION_CHANGE_EVENT, onChange);
    window.removeEventListener('storage', onChange);
  };
}

function read<T>(key: string): T | null {
  const raw = readRaw(key);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

export function rememberCustomer(remember: boolean): void {
  try {
    window.sessionStorage.setItem(CUSTOMER_REMEMBER_KEY, remember ? '1' : '0');
  } catch {
    // The next response falls back to a durable session.
  }
}

export function customerShouldRemember(): boolean {
  try {
    return window.sessionStorage.getItem(CUSTOMER_REMEMBER_KEY) !== '0';
  } catch {
    return true;
  }
}

export const staffSession = {
  get: (): StaffSession | null => readSession<StaffSession>(STAFF_KEY, 'user'),
  set: (session: StaffSession | null, remember = true): void => writeRaw(STAFF_KEY, session, remember),
  token: (): string | null => readSession<StaffSession>(STAFF_KEY, 'user')?.token ?? null,
};

export const customerSession = {
  get: (): CustomerSession | null => readSession<CustomerSession>(CUSTOMER_KEY, 'customer'),
  set: (session: CustomerSession | null, remember = true): void => writeRaw(CUSTOMER_KEY, session, remember),
  token: (): string | null => readSession<CustomerSession>(CUSTOMER_KEY, 'customer')?.token ?? null,
};
